import { RewardAd, RewardAdConfig } from './rewardads';

export interface AdHandlerConfig {
    // O seletor CSS para os elementos que o AdHandler irá gerir (ex: '.hidden-content').
    selector: string;
    // O tipo de evento do DOM que aciona a lógica (ex: 'click').
    event?: string;
    // O caminho da unidade de anúncio para o RewardAd.
    adUnitPath?: string;
    // A frequência em cliques para tentar exibir um anúncio (ex: a cada 4 cliques).
    adFrequency?: number;
    // Indica se o ambiente é de produção para controlar os logs.
    isProdEnv?: boolean;

    // Callback quando o RewardAd está pronto para exibir.
    onAdReady?: (slot: any) => void;
    // Callback quando o RewardAd está prestes a ser exibido.
    onAdShow?: () => void;
    // Callback quando a recompensa é concedida pelo RewardAd.
    onRewardGranted?: (payload: any) => void;
    // Callback quando o RewardAd é fechado.
    onAdClosed?: () => void;
    // Callback quando o RewardAd encontra um erro de slot vazio.
    onAdEmpty?: () => void;
    // Callback quando o RewardAd encontra um erro geral.
    onAdError?: (error: any) => void;
}

interface ElementAdState {
    // O ID único do elemento (dataset.sgAdHandlerId).
    id: string; 
    // Verdadeiro se um anúncio já foi tentado para este elemento.
    hasShown: boolean;
    // Indica se este elemento está elegível para mostrar um anúncio no próximo clique.
    shouldShow: boolean; 
}

export class AdHandler {
    private config: AdHandlerConfig;
    private rewardAd: RewardAd;
    private elementStates: Map<string, ElementAdState> = new Map();
    private elements: ElementAdState[] = [];
    private currentAdFrequency: number;
    private totalClickCount: number = 0;

    constructor(config: AdHandlerConfig) {
        if (!config.selector) {
            this.log('error', 'O "selector" é obrigatório.');
            throw new Error('Selector is required.');
        }
        this.config = {
            event: config.event || 'click',
            adUnitPath: config.adUnitPath || '/22639388115/rewarded_web_example',
            adFrequency: config.adFrequency || 4,
            isProdEnv: config.isProdEnv !== undefined ? config.isProdEnv : true,
            ...config
        };

        this.currentAdFrequency = this.config.adFrequency!;

        const rewardAdConfig: RewardAdConfig = {
            adUnitPath: this.config.adUnitPath!,
            isProdEnv: this.config.isProdEnv,
            onAdReady: this.config.onAdReady,
            onAdShow: this.config.onAdShow,
            onRewardGranted: this.config.onRewardGranted,
            onAdClosed: this.config.onAdClosed,
            onAdEmpty: this.config.onAdEmpty,
            onAdError: this.config.onAdError,
        };

        this.rewardAd = new RewardAd(rewardAdConfig);
        this.rewardAd.loadAd();
        this.initializeElements();
        this.setupEventListeners();
        this.log('log', 'AdHandler inicializado.');
    }

    private log(type: 'log' | 'warn' | 'error' = 'log', message: string, error?: any): void {
        if (this.config.isProdEnv) {
            return;
        }
        const prefix = '[Sg AdHandler]';
        switch (type) {
            case 'log':
                console.log(`${prefix} ${message}`);
                break;
            case 'warn':
                console.warn(`${prefix} ${message}`);
                break;
            case 'error':
                console.error(`${prefix} ${message}`, error || '');
                break;
        }
    }

    private initializeElements(): void {
        const domElements = Array.from(document.querySelectorAll(this.config.selector)) as HTMLElement[];
        
        this.elements = [];
        this.elementStates.clear();
        this.totalClickCount = 0;

        domElements.forEach((element) => {
            if (!element.dataset.sgAdHandlerId) {
                element.dataset.sgAdHandlerId = `sg-adhandler-element-${Math.random().toString(36).substring(2, 9)}`;
            }
            const elementId = element.dataset.sgAdHandlerId;
            
            const state: ElementAdState = {
                id: elementId,
                hasShown: false,
                shouldShow: false,
            };
            this.elementStates.set(elementId, state);
            this.elements.push(state);
        });
        this.log('log', `Inicializado ${this.elements.length} elementos para o seletor '${this.config.selector}'.`);
        this.updateShouldShow();
    }

    private updateShouldShow(): void {
        let adSequenceCounter = 0;
        this.elements.forEach(state => {
            if (!state.hasShown) {
                adSequenceCounter++;
                const shouldShow = (adSequenceCounter % this.currentAdFrequency === 0);
                state.shouldShow = shouldShow;
            } else {
                state.shouldShow = false;
            }
        });
        
        const totalShouldShow = this.elements.filter(s => s.shouldShow).length;
        this.log('log', `Total de elementos que deveriam mostrar anúncio: ${totalShouldShow}`);
    }

    private setupEventListeners(): void {
        document.addEventListener(this.config.event!, (e) => {
            const targetElement = (e.target as HTMLElement).closest(this.config.selector) as HTMLElement;
            if (targetElement && targetElement.dataset.sgAdHandlerId) {
                this.handleAdTrigger(targetElement);
            }
        });
        this.log('log', `Listener para '${this.config.event}' configurado no selector '${this.config.selector}'.`);
    }

    private handleAdTrigger(element: HTMLElement): void {
        const elementId = element.dataset.sgAdHandlerId as string;
        let state = this.elementStates.get(elementId);

        if (!state) {
            this.log('error', `Estado não encontrado para o elemento com ID: ${elementId}.`);
            return;
        }

        if (state.hasShown) {
            this.log('log', `Elemento ${elementId} já mostrou anúncio anteriormente. Ignorando clique.`);
            return;
        }

        this.totalClickCount++;
        this.log('log', `Elemento: ${elementId}. Contagem global de cliques: ${this.totalClickCount}.`);

        const shouldShowAd = (this.totalClickCount % this.currentAdFrequency === 0);
        this.log('log', `Contador: ${this.totalClickCount}. Frequência: ${this.currentAdFrequency}. shouldShow: ${shouldShowAd}`);

        if (shouldShowAd) {
            this.log('log', `A tentar mostrar anúncio para ${elementId} via RewardAd.show().`);
            const adShown = this.rewardAd.show();

            if (adShown) {
                this.log('log', `Anúncio solicitado e exibido para ${elementId}.`);
            } else {
                this.log('log', `RewardAd.show() retornou false para ${elementId}. Anúncio NÃO exibido.`);
            }


            if (this.rewardAd.nextAd) {
                this.rewardAd.nextAd(this.config.adUnitPath!); 
                this.log('log', `A definir o próximo ad unit para o RewardAd.`);
            }
        } else {
            this.log('log', `Contador não é múltiplo de ${this.currentAdFrequency}. Não mostra anúncio para ${elementId}.`);
        }
        state.hasShown = true;
        this.log('log', `Elemento ${elementId} clicado. hasShown = true.`);
        
        this.updateShouldShow();
    }

    public updateFrequency(newFrequency: number): void {
        if (newFrequency > 0) {
            this.currentAdFrequency = newFrequency;
            this.log('log', `Frequência do anúncio atualizada para: ${newFrequency}.`);
            
            this.totalClickCount = 0;
            this.elements.forEach(state => {
                state.hasShown = false; 
            });
            this.updateShouldShow();
            this.log('log', 'Contador global resetado e estados dos elementos recalculados devido à mudança de frequência.');
        } else {
            this.log('warn', 'A frequência deve ser um número positivo.');
        }
    }

    public info(): {
        selector: string;
        event: string;
        adUnitPath: string;
        adFrequency: number;
        isProdEnv?: boolean;
        totalClickCount: number;
        elements: { id: string; hasShown: boolean; shouldShow: boolean }[];
    } {
        return {
            selector: this.config.selector,
            event: this.config.event!,
            adUnitPath: this.config.adUnitPath!,
            adFrequency: this.config.adFrequency!,
            isProdEnv: this.config.isProdEnv,
            totalClickCount: this.totalClickCount,
            elements: this.elements,
        };
    }

    public destroy(): void {
        this.rewardAd.destroy();
        this.log('log', 'AdHandler e RewardAd destruídos.');
    }
}